import { useCallback } from "react"
import { useNavigate } from "@tanstack/react-router"

import { useChat } from "@/features/chat"
import { WebappRoutes } from "@/shared/lib/webapp-routes"

import { ReportIds, type ReportItem } from "../config/reports-data"
import { isPurchasableReport } from "../config/report-plans"
import { useReportPurchase } from "./use-report-purchase"
import { useReportPurchaseStore } from "./report-purchase-store"
import { hasSeenReveal, markRevealSeen } from "./report-reveal-seen"

interface UseReportCtaArgs {
  userMainSubscriptionId: string | null
}

export function useReportCta({ userMainSubscriptionId }: UseReportCtaArgs) {
  const navigate = useNavigate()
  const { isPalmReadingLocked, setPalmReportModalOpen } = useChat()
  const { purchase, isReportPurchasing } = useReportPurchase({ userMainSubscriptionId })
  const showReveal = useReportPurchaseStore((s) => s.showReveal)

  const openPurchasedReport = useCallback(
    (report: ReportItem) => {
      // Love Match (truths) + Astrology (star): the "ready to reveal" popover is a
      // one-time moment — show it the first time the finished report is tapped,
      // mark it seen (localStorage), and open the report directly on every later tap.
      const hasRevealPopover =
        report.purchaseType === "truths" ||
        report.purchaseType === "star" ||
        report.purchaseType === "soulmate" ||
        report.purchaseType === "aura"
      if (hasRevealPopover && !hasSeenReveal(report.id)) {
        markRevealSeen(report.id)
        showReveal(report)
        return
      }

      // Match the Palmistry tab: while the 10‑min prepare lock is active, open the
      // countdown sheet instead of navigating into the scan flow again.
      if (report.id === ReportIds.Palmistry && isPalmReadingLocked) {
        setPalmReportModalOpen(true)
        return
      }

      if (report.delivery === "in-app" && report.route) {
        navigate({ to: report.route })
        return
      }

      // email / download reports open the in-app PDF preview screen
      navigate({ to: WebappRoutes.REPORT_VIEW, search: { report: report.id } })
    },
    [navigate, showReveal, isPalmReadingLocked, setPalmReportModalOpen],
  )

  const handleReportCta = useCallback(
    (report: ReportItem, purchased: boolean) => {
      if (purchased) {
        openPurchasedReport(report)
        return
      }

      if (isPurchasableReport(report.purchaseType)) {
        void purchase(report)
      }
    },
    [openPurchasedReport, purchase],
  )

  return {
    handleReportCta,
    isReportPurchasing,
  }
}
